/**
 * Settings → AI Agents — dynamic multi-agent management (no hardcoded roles).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Archive,
  Bot,
  CheckCircle2,
  Copy,
  Download,
  Loader2,
  Plus,
  Power,
  PowerOff,
  Search,
  Sparkles,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { agentsApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Shell } from "@/pages/settings/SettingsPages";

const WIZARD_STEPS = [
  "Basic",
  "Prompt",
  "Capabilities",
  "Resources",
  "Routing",
  "Review",
];

const EMPTY_DRAFT = {
  name: "",
  description: "",
  avatar_emoji: "🤖",
  color: "#0f766e",
  category: "",
  version_label: "1.0.0",
  status: "draft",
  system_prompt: "",
  model: "",
  temperature: 0.4,
  max_tokens: 2048,
  tags: [],
  capabilities: [],
  permissions: [],
  resources: [],
  routing_rules: [{ rule_type: "manual_only", priority: 100, enabled: true, config: {} }],
  collaborations: [],
  is_default: false,
  template_id: null,
};

function statusBadge(status) {
  const map = {
    active: "bg-emerald-50 text-emerald-800 border-emerald-200",
    draft: "bg-slate-50 text-slate-700 border-slate-200",
    disabled: "bg-amber-50 text-amber-800 border-amber-200",
    archived: "bg-rose-50 text-rose-800 border-rose-200",
  };
  return map[status] || map.draft;
}

function _orNull(v) { return v || null; }
function _orArr(v) { return v || []; }

function buildAgentPayload(draft, editId) {
  const payload = {
    name: draft.name.trim(),
    description: _orNull(draft.description),
    avatar_emoji: _orNull(draft.avatar_emoji),
    color: _orNull(draft.color),
    category: _orNull(draft.category),
    version_label: draft.version_label || "1.0.0",
    status: draft.status || "draft",
    system_prompt: draft.system_prompt || "",
    model: _orNull(draft.model),
    temperature: Number(draft.temperature ?? 0.4),
    max_tokens: Number(draft.max_tokens ?? 2048),
    tags: _orArr(draft.tags),
    capabilities: _orArr(draft.capabilities),
    permissions: _orArr(draft.permissions),
    resources: _orArr(draft.resources),
    routing_rules: _orArr(draft.routing_rules),
    collaborations: _orArr(draft.collaborations),
    is_default: Boolean(draft.is_default),
  };
  if (!editId && draft.template_id) payload.template_id = draft.template_id;
  return payload;
}

function buildCreateDraft(EMPTY_DRAFT, template) {
  const next = { ...EMPTY_DRAFT };
  if (template) {
    const prefills = template.prefills || {};
    next.name = template.name === "Blank Agent" ? "" : `${template.name}`;
    next.description = template.description || "";
    next.category = template.category || "";
    next.tags = template.tags || [];
    next.system_prompt = prefills.system_prompt || "";
    next.capabilities = (prefills.capabilities || []).map((key) =>
      typeof key === "string" ? { key, enabled: true } : key,
    );
    next.permissions = (prefills.permissions || []).map((key) =>
      typeof key === "string" ? { key, granted: true } : key,
    );
    next.routing_rules = prefills.routing_rules || next.routing_rules;
    next.template_id = template.id;
  }
  return next;
}

function buildEditDraft(EMPTY_DRAFT, agent) {
  return {
    ...EMPTY_DRAFT,
    ...agent,
    capabilities: agent.capabilities || [],
    permissions: agent.permissions || [],
    resources: agent.resources || [],
    routing_rules: agent.routing_rules?.length
      ? agent.routing_rules
      : EMPTY_DRAFT.routing_rules,
    collaborations: agent.collaborations || [],
  };
}

export function SettingsAiAgents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [agents, setAgents] = useState([]);
  const [catalog, setCatalog] = useState({ capabilities: [], permissions: [], routing_rule_types: [] });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testMessage, setTestMessage] = useState("Hello — please introduce yourself briefly.");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [activity, setActivity] = useState([]);
  const importRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [list, cat, tmpl] = await Promise.all([
        agentsApi.list({
          q: q || undefined,
          status: statusFilter === "all" ? undefined : statusFilter,
          include_archived: statusFilter === "archived" || statusFilter === "all",
        }),
        agentsApi.catalog(),
        agentsApi.templates(),
      ]);
      setAgents(list.agents || []);
      setCatalog(cat || {});
      setTemplates(tmpl.templates || []);
    } catch (err) {
      toast.error(err?.message || "Failed to load agents");
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      openCreate();
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const filtered = useMemo(() => agents, [agents]);

  function openCreate(template = null) {
    const next = buildCreateDraft(EMPTY_DRAFT, template);
    setEditId(null);
    setDraft(next);
    setWizardStep(0);
    setWizardOpen(true);
  }

  async function openEdit(agent) {
    setEditId(agent.id);
    setDraft(buildEditDraft(EMPTY_DRAFT, agent));
    setWizardStep(0);
    setWizardOpen(true);
  }

  async function openDetail(agent) {
    try {
      const full = await agentsApi.get(agent.id);
      setDetail(full);
      const act = await agentsApi.activity(agent.id, { limit: 30 });
      setActivity(act.activity || []);
    } catch (err) {
      toast.error(err?.message || "Failed to load agent");
    }
  }

  async function saveWizard() {
    if (!String(draft.name || "").trim()) {
      toast.error("Agent name is required");
      setWizardStep(0);
      return;
    }
    setSaving(true);
    try {
      const payload = buildAgentPayload(draft, editId);
      if (editId) {
        await agentsApi.update(editId, payload);
        toast.success("Agent updated");
      } else {
        await agentsApi.create(payload);
        toast.success("Agent created");
      }
      setWizardOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function setStatus(agent, status) {
    try {
      await agentsApi.setStatus(agent.id, status);
      toast.success(`Agent ${status}`);
      refresh();
    } catch (err) {
      toast.error(err?.message || "Status update failed");
    }
  }

  async function duplicate(agent) {
    try {
      await agentsApi.duplicate(agent.id);
      toast.success("Agent duplicated");
      refresh();
    } catch (err) {
      toast.error(err?.message || "Duplicate failed");
    }
  }

  async function remove(agent) {
    if (!window.confirm(`Archive agent “${agent.name}”?`)) return;
    try {
      await agentsApi.remove(agent.id);
      toast.success("Agent archived");
      refresh();
    } catch (err) {
      toast.error(err?.message || "Delete failed");
    }
  }

  async function exportAgent(agent) {
    try {
      const doc = await agentsApi.export(agent.id);
      const blob = new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${agent.slug || "agent"}.sellyx-agent.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.message || "Export failed");
    }
  }

  async function importAgentFile(file) {
    try {
      const text = await file.text();
      const document = JSON.parse(text);
      await agentsApi.import(document);
      toast.success("Agent imported");
      refresh();
    } catch (err) {
      toast.error(err?.message || "Import failed");
    }
  }

  async function runTest() {
    if (!detail?.id) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await agentsApi.test(detail.id, testMessage);
      setTestResult(res);
    } catch (err) {
      toast.error(err?.message || "Test failed");
    } finally {
      setTesting(false);
    }
  }

  function toggleCapability(key) {
    setDraft((prev) => {
      const existing = prev.capabilities || [];
      const has = existing.some((c) => (c.key || c) === key);
      if (has) {
        return {
          ...prev,
          capabilities: existing.filter((c) => (c.key || c) !== key),
        };
      }
      return { ...prev, capabilities: [...existing, { key, enabled: true }] };
    });
  }

  function togglePermission(key) {
    setDraft((prev) => {
      const existing = prev.permissions || [];
      const has = existing.some((p) => (p.key || p) === key);
      if (has) {
        return {
          ...prev,
          permissions: existing.filter((p) => (p.key || p) !== key),
        };
      }
      return { ...prev, permissions: [...existing, { key, granted: true }] };
    });
  }

  return (
    <Shell title="AI Agents" desc="Create and manage your own AI workforce. No predefined business roles — every agent is configuration-driven.">
      <div className="px-6 py-4 flex justify-end gap-2">
        <input
          ref={importRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) importAgentFile(f);
            e.target.value = "";
          }}
        />
        <Button type="button" variant="outline" size="sm" onClick={() => importRef.current?.click()}>
          <Upload size={14} className="mr-1" /> Import
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => openCreate()}
          data-testid="ai-agents-create"
        >
          <Plus size={14} className="mr-1" /> Create Agent
        </Button>
      </div>

      <div className="px-6 pb-8 space-y-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              className="pl-8 h-9"
              placeholder="Search agents…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="ai-agents-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] h-9">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {templates.length > 0 && (
          <div>
            <div className="text-[12px] font-medium text-slate-600 mb-2">Start from a template (prefills only)</div>
            <div className="flex flex-wrap gap-2">
              {templates.slice(0, 8).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => openCreate(t)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-left hover:border-teal-300 hover:bg-teal-50/40 transition-colors"
                  data-testid={`ai-agent-template-${t.id}`}
                >
                  <div className="text-[12.5px] font-medium text-slate-800">{t.name}</div>
                  <div className="text-[11px] text-slate-500 line-clamp-1">{t.description}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {(() => {
          if (loading) {
            return (
              <div className="flex items-center gap-2 text-slate-500 text-sm py-12 justify-center">
                <Loader2 className="animate-spin" size={16} /> Loading agents…
              </div>
            );
          }
          if (filtered.length === 0) {
            return (
              <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 px-6 py-14 text-center">
                <Bot className="mx-auto text-slate-400 mb-3" size={28} />
                <div className="text-sm font-medium text-slate-800">No agents yet</div>
                <div className="text-[12.5px] text-slate-500 mt-1 max-w-md mx-auto">
                  Create your first agent from scratch or start from an optional template.
                  Templates only prefill values — they are not built-in permanent agents.
                </div>
                <Button className="mt-4" size="sm" onClick={() => openCreate()}>
                  <Sparkles size={14} className="mr-1" /> Create Agent
                </Button>
              </div>
            );
          }
          return (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="ai-agents-list">
            {filtered.map((agent) => (
              <div
                key={agent.id}
                className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:border-slate-300 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-lg shrink-0"
                    style={{ backgroundColor: `${agent.color || "#0f766e"}22` }}
                  >
                    {agent.avatar_emoji || "🤖"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="font-semibold text-sm text-slate-900 truncate hover:underline text-left"
                        onClick={() => openDetail(agent)}
                      >
                        {agent.name}
                      </button>
                      {agent.is_default && (
                        <Badge variant="outline" className="text-[10px]">Default</Badge>
                      )}
                    </div>
                    <div className="text-[11.5px] text-slate-500 line-clamp-2 mt-0.5">
                      {agent.description || "No description"}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <Badge variant="outline" className={cn("text-[10px]", statusBadge(agent.status))}>
                    {agent.status}
                  </Badge>
                  {agent.category && (
                    <Badge variant="outline" className="text-[10px]">{agent.category}</Badge>
                  )}
                  <span className="text-[10.5px] text-slate-400 ml-auto">
                    {(agent.capabilities || []).filter((c) => c.enabled !== false).length} capabilities
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => openDetail(agent)}>
                    View
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => openEdit(agent)}>
                    Edit
                  </Button>
                  {agent.status === "active" ? (
                    <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setStatus(agent, "disabled")}>
                      <PowerOff size={12} className="mr-1" /> Disable
                    </Button>
                  ) : agent.status !== "archived" ? (
                    <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setStatus(agent, "active")}>
                      <Power size={12} className="mr-1" /> Enable
                    </Button>
                  ) : null}
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => duplicate(agent)}>
                    <Copy size={12} />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => exportAgent(agent)}>
                    <Download size={12} />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px] text-rose-600" onClick={() => remove(agent)}>
                    <Archive size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          );
        })()}
      </div>

      {/* Create / Edit Wizard */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Agent" : "Create Agent"}</DialogTitle>
          </DialogHeader>
          <div className="flex gap-1 mb-4 overflow-x-auto">
            {WIZARD_STEPS.map((label, idx) => (
              <button
                key={label}
                type="button"
                onClick={() => setWizardStep(idx)}
                className={cn(
                  "px-2.5 py-1 rounded-md text-[11px] whitespace-nowrap border",
                  wizardStep === idx
                    ? "bg-teal-700 text-white border-teal-700"
                    : "bg-white text-slate-600 border-slate-200",
                )}
              >
                {idx + 1}. {label}
              </button>
            ))}
          </div>

          {wizardStep === 0 && (
            <div className="space-y-3">
              <div>
                <Label>Agent Name</Label>
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="ai-agent-name" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Avatar / Emoji</Label>
                  <Input value={draft.avatar_emoji || ""} onChange={(e) => setDraft({ ...draft, avatar_emoji: e.target.value })} />
                </div>
                <div>
                  <Label>Color</Label>
                  <Input type="color" value={draft.color || "#0f766e"} onChange={(e) => setDraft({ ...draft, color: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category (optional)</Label>
                  <Input value={draft.category || ""} onChange={(e) => setDraft({ ...draft, category: e.target.value })} />
                </div>
                <div>
                  <Label>Version</Label>
                  <Input value={draft.version_label || ""} onChange={(e) => setDraft({ ...draft, version_label: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={Boolean(draft.is_default)} onCheckedChange={(v) => setDraft({ ...draft, is_default: v })} />
                <Label>Set as default agent for automatic fallback</Label>
              </div>
            </div>
          )}

          {wizardStep === 1 && (
            <div className="space-y-3">
              <Label>System Prompt</Label>
              <Textarea
                rows={14}
                value={draft.system_prompt || ""}
                onChange={(e) => setDraft({ ...draft, system_prompt: e.target.value })}
                placeholder="Define any role or behaviour. The platform does not impose a predefined business role."
                data-testid="ai-agent-system-prompt"
              />
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Model</Label>
                  <Input value={draft.model || ""} onChange={(e) => setDraft({ ...draft, model: e.target.value })} placeholder="Optional" />
                </div>
                <div>
                  <Label>Temperature</Label>
                  <Input type="number" step="0.1" min="0" max="2" value={draft.temperature ?? 0.4}
                    onChange={(e) => setDraft({ ...draft, temperature: Number(e.target.value) })} />
                </div>
                <div>
                  <Label>Max tokens</Label>
                  <Input type="number" value={draft.max_tokens ?? 2048}
                    onChange={(e) => setDraft({ ...draft, max_tokens: Number(e.target.value) })} />
                </div>
              </div>
            </div>
          )}

          {wizardStep === 2 && (
            <div className="space-y-4">
              <div>
                <div className="text-[12px] font-medium text-slate-700 mb-2">Capabilities</div>
                <div className="grid grid-cols-2 gap-2">
                  {(catalog.capabilities || []).map((cap) => {
                    const on = (draft.capabilities || []).some((c) => (c.key || c) === cap.key);
                    return (
                      <label key={cap.key} className={cn("flex items-center gap-2 rounded-lg border px-2.5 py-2 text-[12px] cursor-pointer", on ? "border-teal-300 bg-teal-50/50" : "border-slate-200")}>
                        <input type="checkbox" checked={on} onChange={() => toggleCapability(cap.key)} />
                        {cap.label}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-[12px] font-medium text-slate-700 mb-2">Permissions</div>
                <div className="grid grid-cols-2 gap-2">
                  {(catalog.permissions || []).map((perm) => {
                    const on = (draft.permissions || []).some((p) => (p.key || p) === perm.key);
                    return (
                      <label key={perm.key} className={cn("flex items-center gap-2 rounded-lg border px-2.5 py-2 text-[12px] cursor-pointer", on ? "border-teal-300 bg-teal-50/50" : "border-slate-200")}>
                        <input type="checkbox" checked={on} onChange={() => togglePermission(perm.key)} />
                        {perm.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {wizardStep === 3 && (
            <div className="space-y-3">
              <p className="text-[12.5px] text-slate-600">
                Connect resources this agent may access. Agents only receive resources you explicitly assign.
              </p>
              <div>
                <Label>Knowledge base IDs (comma-separated)</Label>
                <Input
                  placeholder="kb-id-1, kb-id-2"
                  value={(draft.resources || [])
                    .filter((r) => r.resource_type === "knowledge_base")
                    .map((r) => r.resource_id)
                    .join(", ")}
                  onChange={(e) => {
                    const ids = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                    const other = (draft.resources || []).filter((r) => r.resource_type !== "knowledge_base");
                    setDraft({
                      ...draft,
                      resources: [
                        ...other,
                        ...ids.map((id) => ({
                          resource_type: "knowledge_base",
                          resource_id: id,
                          access_level: "read",
                        })),
                      ],
                    });
                  }}
                />
              </div>
              <div>
                <Label>Messaging channel connection IDs</Label>
                <Input
                  placeholder="connection-uuid-1, …"
                  value={(draft.resources || [])
                    .filter((r) => r.resource_type === "messaging_channel")
                    .map((r) => r.resource_id)
                    .join(", ")}
                  onChange={(e) => {
                    const ids = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                    const other = (draft.resources || []).filter((r) => r.resource_type !== "messaging_channel");
                    setDraft({
                      ...draft,
                      resources: [
                        ...other,
                        ...ids.map((id) => ({
                          resource_type: "messaging_channel",
                          resource_id: id,
                          access_level: "execute",
                        })),
                      ],
                    });
                  }}
                />
              </div>
            </div>
          )}

          {wizardStep === 4 && (
            <div className="space-y-3">
              <Label>Primary routing mode</Label>
              <Select
                value={(draft.routing_rules || [])[0]?.rule_type || "manual_only"}
                onValueChange={(v) => setDraft({
                  ...draft,
                  routing_rules: [{
                    rule_type: v,
                    priority: 50,
                    enabled: true,
                    config: (() => {
                      if (v === "keywords") return { keywords: ["help", "support"] };
                      if (v === "channel") return { channels: ["email", "whatsapp", "telegram"] };
                      return {};
                    })(),
                  }],
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(catalog.routing_rule_types || []).map((r) => (
                    <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[12px] text-slate-500">
                Future routing methods can be added without changing the routing engine.
                Agent-to-agent invoke is configured on the agent detail after create.
              </p>
            </div>
          )}

          {wizardStep === 5 && (
            <div className="space-y-2 text-[13px] text-slate-700 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div><span className="font-medium">Name:</span> {draft.name || "—"}</div>
              <div><span className="font-medium">Category:</span> {draft.category || "—"}</div>
              <div><span className="font-medium">Capabilities:</span> {(draft.capabilities || []).length}</div>
              <div><span className="font-medium">Permissions:</span> {(draft.permissions || []).length}</div>
              <div><span className="font-medium">Resources:</span> {(draft.resources || []).length}</div>
              <div><span className="font-medium">Routing:</span> {(draft.routing_rules || [])[0]?.rule_type || "—"}</div>
              <div className="pt-2 border-t border-slate-200">
                <div className="font-medium mb-1">System prompt preview</div>
                <pre className="whitespace-pre-wrap text-[11.5px] text-slate-600 max-h-40 overflow-auto">
                  {draft.system_prompt || "(empty)"}
                </pre>
              </div>
            </div>
          )}

          <div className="flex justify-between pt-4 border-t border-slate-100 mt-4">
            <Button type="button" variant="outline" disabled={wizardStep === 0} onClick={() => setWizardStep((s) => Math.max(0, s - 1))}>
              Back
            </Button>
            <div className="flex gap-2">
              {wizardStep < WIZARD_STEPS.length - 1 ? (
                <Button type="button" onClick={() => setWizardStep((s) => Math.min(WIZARD_STEPS.length - 1, s + 1))}>
                  Next
                </Button>
              ) : (
                <Button type="button" onClick={saveWizard} disabled={saving} data-testid="ai-agent-save">
                  {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : <CheckCircle2 className="mr-1" size={14} />}
                  {editId ? "Save changes" : "Create agent"}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail / Activity / Test */}
      <Dialog open={Boolean(detail)} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span>{detail.avatar_emoji || "🤖"}</span> {detail.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-[13px]">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className={statusBadge(detail.status)}>{detail.status}</Badge>
                  {detail.is_default && <Badge variant="outline">Default</Badge>}
                  <Badge variant="outline">v{detail.version_label}</Badge>
                </div>
                <p className="text-slate-600">{detail.description || "No description"}</p>
                <div>
                  <div className="font-medium text-slate-800 mb-1">System prompt</div>
                  <pre className="whitespace-pre-wrap text-[12px] bg-slate-50 border rounded-lg p-3 max-h-40 overflow-auto">
                    {detail.system_prompt || "(empty)"}
                  </pre>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setTestOpen(true); setTestResult(null); }}>Test Agent</Button>
                  <Button size="sm" variant="outline" onClick={() => { openEdit(detail); setDetail(null); }}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => exportAgent(detail)}>Export</Button>
                </div>
                <div>
                  <div className="font-medium text-slate-800 mb-2">Recent activity</div>
                  {activity.length === 0 ? (
                    <div className="text-slate-500 text-[12px]">No activity yet.</div>
                  ) : (
                    <ul className="space-y-1.5 max-h-48 overflow-auto">
                      {activity.map((ev) => (
                        <li key={ev.id} className="text-[12px] text-slate-600 border-b border-slate-100 pb-1">
                          <span className="font-medium">{ev.event_type}</span>
                          <span className="text-slate-400 ml-2">{ev.created_at}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test Agent</DialogTitle>
          </DialogHeader>
          <Textarea rows={4} value={testMessage} onChange={(e) => setTestMessage(e.target.value)} />
          <Button onClick={runTest} disabled={testing}>
            {testing ? <Loader2 className="animate-spin mr-1" size={14} /> : null}
            Run test
          </Button>
          {testResult && (
            <pre className="text-[12px] bg-slate-50 border rounded-lg p-3 whitespace-pre-wrap max-h-60 overflow-auto">
              {JSON.stringify(testResult, null, 2)}
            </pre>
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

SettingsAiAgents.propTypes = {};

export default SettingsAiAgents;
